'use client'

import React, { ForwardedRef, forwardRef, useEffect, useState } from 'react'
import * as SelectRadix from '@radix-ui/react-select'
import { VariantProps } from 'class-variance-authority'

import { cn } from '../../utils/cn'
import { ChevronDownIcon } from '../icons'
import { inputColor, inputVariants } from '../input/input'

export interface SelectProps
  extends SelectRadix.SelectProps,
    VariantProps<typeof inputVariants> {
  id?: string
  /** Accessible label that appears above the input (required if no aria-label prop is present) */
  label?: string
  /** Displays this message below the input in gray (if there is no errorMessage) */
  hint?: string
  /** sets input as invalid */
  invalid?: boolean
  /** Displays this message in red below the input */
  errorMessage?: string

  children: React.ReactNode

  color?: 'white' | 'gray'

  className?: string

  style?: any

  placeholder?: string

  /** removes bottom padding unless there is a hint or error */
  dense?: boolean

  ['aria-label']?: string

  onBlur?: () => void
}

const Select = forwardRef(
  (
    {
      label,
      hint,
      invalid,
      errorMessage,
      children,
      color,
      className,
      style,
      placeholder,
      variantSize,
      dense,
      onBlur,
      ...props
    }: SelectProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const [uid] = useState<string>(React.useId())

    useEffect(() => {
      if (!props['aria-label'] && !label) {
        throw new Error(
          'Input elements require either a label or an aria-label for accessibility.'
        )
      }
    }, [])

    const renderDescription = () => {
      if (errorMessage && invalid) {
        return (
          <span
            id={`message-${uid}`}
            role="alert"
            className="text-xs text-error-800"
          >
            {errorMessage}
          </span>
        )
      }
      if (hint) {
        return (
          <span id={`message-${uid}`} className="text-xs">
            {hint}
          </span>
        )
      } else if (!dense) {
        return (
          <span role="presentation" style={{ visibility: 'hidden' }}>
            ...
          </span>
        )
      }
    }

    return (
      <div ref={ref}>
        {label && (
          <label
            htmlFor={`input-${uid}`}
            className={cn(
              invalid && 'text-error-800',
              'font-semibold text-sm',
              'block mb-1'
            )}
          >
            {label}
          </label>
        )}
        <div>
          <SelectRadix.Root {...props}>
            <SelectRadix.Trigger
              onBlur={onBlur}
              placeholder={props['aria-label']}
              id={`input-${uid}`}
              aria-describedby={`message-${uid}`}
              aria-label={!label ? props['aria-label'] : undefined}
              className={cn(
                invalid
                  ? 'border-error-800'
                  : 'border-neutral-400 hover:border-neutral-500',
                inputVariants({ variantSize }),
                'items-center justify-between',
                className,
                inputColor(color, invalid)
              )}
              style={style || {}}
              data-testid={`${label || props['aria-label']}-select`}
            >
              <span className="truncate">
                <SelectRadix.Value
                  placeholder={placeholder}
                  data-testid="select-value"
                />
              </span>
              <SelectRadix.Icon>
                <ChevronDownIcon width={20} height={20} />
              </SelectRadix.Icon>
            </SelectRadix.Trigger>

            <SelectRadix.Portal>
              <SelectRadix.Content
                className={cn(
                  'max-w-[90vw] rounded-md border border-neutral-200 bg-white p-1 drop-shadow-lg'
                )}
                style={{ zIndex: 1000 }}
              >
                <SelectRadix.Viewport>{children}</SelectRadix.Viewport>
                <SelectRadix.ScrollDownButton className="text-violet11 flex h-[1.5625rem] cursor-default items-center justify-center bg-white">
                  <ChevronDownIcon />
                </SelectRadix.ScrollDownButton>
              </SelectRadix.Content>
            </SelectRadix.Portal>
          </SelectRadix.Root>
        </div>

        {renderDescription()}
      </div>
    )
  }
)

Select.displayName = 'Select'

export { Select }
